"use client";

import { useState, type CSSProperties, type FormEvent } from "react";
import { useI18n } from "@/i18n/context";
import { SiteNav } from "@/components/SiteNav";
import { useSiteEffects } from "@/components/useSiteEffects";
import { useCaptcha } from "@/components/useCaptcha";
import { CaptchaWidget } from "@/components/CaptchaWidget";
import { HERO_RINGS, WHEN_RINGS } from "@/components/ringData";
import { WaitlistPill } from "@/components/waitlist/WaitlistPill";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/links";

// Italic serif emphasis used inline where the section CSS doesn't already style <em>.
const emSerif: CSSProperties = {
  fontFamily: "var(--font-serif), serif",
  fontStyle: "italic",
  fontWeight: 300,
};

const symbolMask: CSSProperties = {
  WebkitMask: "url('/assets/woozoo-symbol.png') no-repeat center / contain",
  mask: "url('/assets/woozoo-symbol.png') no-repeat center / contain",
};

export default function Home() {
  const { t, locale } = useI18n();
  useSiteEffects();
  const captcha = useCaptcha();
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "sending") return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    setStatus("sending");
    try {
      const altcha = await captcha.ensureToken();
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: String(fd.get("firstName") ?? ""),
          lastName: String(fd.get("lastName") ?? ""),
          email: String(fd.get("email") ?? ""),
          subject: String(fd.get("subject") ?? ""),
          message: String(fd.get("message") ?? ""),
          locale,
          altcha,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      form.reset();
      captcha.reset();
    } catch {
      setStatus("error");
      captcha.reset();
    }
  };

  // Phone-mockup copy is bilingual; pick the right language explicitly.
  const tp = (en: string, fr: string) => (locale === "fr" ? fr : en);

  return (
    <>
      <WaitlistPill />
      <SiteNav />

      {/* ================== HERO ================== */}
      <section
        className="hero"
        id="home"
        style={{ backgroundColor: "rgb(184, 207, 208)", color: "rgb(161, 184, 185)" }}
      >
        <div className="hero-rings" aria-hidden="true" data-parallax="0.25" style={{ opacity: 0.65 }}>
          <svg viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
            <defs>
              <radialGradient id="centerMask" cx="50%" cy="50%" r="9%">
                <stop offset="0%" stopColor="#000" stopOpacity="1" />
                <stop offset="60%" stopColor="#000" stopOpacity="1" />
                <stop offset="100%" stopColor="#000" stopOpacity="0" />
              </radialGradient>
              <mask id="centerHole">
                <rect width="800" height="800" fill="#fff" />
                <rect width="800" height="800" fill="url(#centerMask)" />
              </mask>
            </defs>
            <g fill="none" stroke="#A8BCC0" strokeOpacity="0.9" strokeWidth="6" mask="url(#centerHole)">
              {HERO_RINGS.map((points, i) => (
                <polygon key={i} points={points} />
              ))}
            </g>
          </svg>
        </div>

        <div className="hero-symbol" aria-hidden="true" style={{ ...symbolMask, background: "var(--navy)" }} />

        <div className="hero-inner">
          <div className="eyebrow">{t("Public digital identity · revealed")}</div>
          <h1 className="display">
            Know <em>who&rsquo;s</em> who.
          </h1>
          <p className="lede">
            {t(
              "WooZoo reveals a person's public digital identity — clear, objective, structured. Nothing invented. Nothing interpreted.",
            )}
          </p>

          <div className="cta-row">
            <a
              href={APP_STORE_URL || "#"}
              target={APP_STORE_URL ? "_blank" : undefined}
              rel={APP_STORE_URL ? "noopener noreferrer" : undefined}
              className="btn-store"
              aria-label="Download on the App Store"
            >
              <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 12.79c-.03-2.96 2.42-4.39 2.53-4.46-1.38-2.02-3.53-2.3-4.29-2.33-1.82-.18-3.56 1.07-4.49 1.07-.94 0-2.36-1.05-3.88-1.02-2 .03-3.84 1.16-4.86 2.94-2.07 3.59-.53 8.9 1.49 11.81.99 1.43 2.16 3.02 3.7 2.97 1.49-.06 2.05-.96 3.85-.96 1.8 0 2.31.96 3.88.93 1.6-.03 2.61-1.44 3.59-2.88 1.13-1.65 1.59-3.25 1.62-3.34-.04-.02-3.11-1.19-3.14-4.73zM14.32 4.18c.82-.99 1.37-2.37 1.22-3.74-1.18.05-2.6.78-3.45 1.77-.76.87-1.43 2.27-1.25 3.61 1.31.1 2.66-.67 3.48-1.64z" />
              </svg>
              <span className="label">
                <small>{t("Download on the")}</small>
                <strong>{t("App Store")}</strong>
              </span>
            </a>
            <a
              href={GOOGLE_PLAY_URL || "#"}
              target={GOOGLE_PLAY_URL ? "_blank" : undefined}
              rel={GOOGLE_PLAY_URL ? "noopener noreferrer" : undefined}
              className="btn-store"
              aria-label="Get it on Google Play"
            >
              <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.6 1.1c-.4.4-.6 1-.6 1.7v18.4c0 .7.2 1.3.6 1.7l.1.1L14 12.6v-.2L3.7 1.1zM17.8 16.4l-3.5-3.5v-.4l3.5-3.5.1.1 4.1 2.3c1.2.7 1.2 1.8 0 2.5l-4.2 2.5zM14.3 12.5l-10.7 10.7c.4.4 1.1.5 1.8.1l12.6-7.2-3.7-3.6zM4.4.8C3.7.4 3 .5 2.6.9L13.3 11.6l3.7-3.7L4.4.8z" />
              </svg>
              <span className="label">
                <small>{t("Get it on")}</small>
                <strong>{t("Google Play")}</strong>
              </span>
            </a>
          </div>
        </div>

        <div className="hero-scroll" aria-hidden="true" />
      </section>

      {/* ================== CONTEXT ================== */}
      <section className="context" id="context">
        <div className="container">
          <div className="eyebrow">{t("01 · The Context")}</div>
          <div className="context-grid">
            <div>
              <h2 className="display">{t("We have access to everything.")}</h2>
              <p>{t("Except a clear gaze on the people we are about to meet, hire, or invest in.")}</p>
            </div>
            <div className="context-chips">
              <div className="chip">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="7" width="18" height="14" rx="2" />
                  <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" />
                </svg>{" "}
                {t("Career path")}
              </div>
              <div className="chip">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h13v16H4z" />
                  <path d="M17 8h3v10a2 2 0 0 1-2 2h-1" />
                  <path d="M7 8h7M7 12h7M7 16h4" />
                </svg>{" "}
                {t("Press articles")}
              </div>
              <div className="chip">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 21s-7-4.5-7-11a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 6.5-7 11-7 11z" />
                </svg>{" "}
                {t("Hobbies & passions")}
              </div>
              <div className="chip">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 9l10-5 10 5-10 5z" />
                  <path d="M6 11v4c0 1.5 2.5 3 6 3s6-1.5 6-3v-4M22 9v6" />
                </svg>{" "}
                {t("Education")}
              </div>
              <div className="chip">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="6" cy="6" r="2.5" />
                  <circle cx="18" cy="6" r="2.5" />
                  <circle cx="12" cy="18" r="2.5" />
                  <path d="M7.5 7.5l3 8.5M16.5 7.5l-3 8.5M8 6h8" />
                </svg>{" "}
                {t("Public profiles")}
              </div>
              <div className="chip">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
                  <path d="M5 4h2v3a2 2 0 0 1-2-2zM17 4h2v1a2 2 0 0 1-2 2zM9 14h6v3H9zM7 20h10" />
                </svg>{" "}
                {t("Awards & mandates")}
              </div>
            </div>
          </div>

          <div className="pivot">
            <p className="display">
              {t("Yet on people — we still walk in")} <em style={emSerif}>{t("blind")}</em>.
            </p>
          </div>
        </div>
      </section>

      {/* ================== GAZE ================== */}
      <section className="gaze" id="gaze">
        <div className="container">
          <div className="gaze-grid">
            <div>
              <div className="eyebrow">{t("02 · A Clear Gaze")}</div>
              <h2 className="display">{t("No opinion. No interpretation.")}</h2>
              <p style={{ color: "var(--ink-muted)", maxWidth: 440 }}>
                {t("WooZoo relies exclusively on publicly available information and verifiable sources.")}
              </p>
              <ul className="principles">
                <li>{t("No self-declared claims")}</li>
                <li>{t("No rumors")}</li>
                <li>{t("No fabricated content")}</li>
              </ul>
              <p className="note">
                {t("WooZoo creates nothing.")}
                <br />
                <strong style={{ color: "var(--navy)" }}>
                  {t("It reveals and structures what already exists.")}
                </strong>
              </p>
            </div>

            {/* iPhone mockup — demo content kept verbatim from the mockup. */}
            <div className="iphone-stage" data-parallax="-0.1">
              <div className="iphone">
                <div className="island" />
                <div className="screen">
                  <div className="app-screen">
                    <div className="status">
                      <span>9:41</span>
                      <span className="icons" aria-hidden="true">
                        <svg width="16" height="10" viewBox="0 0 16 10" fill="currentColor">
                          <rect x="0" y="6" width="2.4" height="4" rx="0.4" />
                          <rect x="3.6" y="4" width="2.4" height="6" rx="0.4" />
                          <rect x="7.2" y="2" width="2.4" height="8" rx="0.4" />
                          <rect x="10.8" y="0" width="2.4" height="10" rx="0.4" />
                        </svg>
                        <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="currentColor" strokeWidth="1.2">
                          <path d="M1 4 Q7 -1 13 4" />
                          <path d="M3 6 Q7 2 11 6" />
                          <path d="M5 8 Q7 6 9 8" />
                        </svg>
                        <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
                          <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke="currentColor" />
                          <rect x="2" y="2" width="15" height="6" rx="1" fill="currentColor" />
                          <rect x="19.5" y="3.5" width="1.5" height="3" rx="0.5" fill="currentColor" />
                        </svg>
                      </span>
                    </div>
                    <div className="app-nav">
                      <span className="icon-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </span>
                      <span className="brand">WOOZOO</span>
                      <span className="icon-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <line x1="3" y1="7" x2="21" y2="7" />
                          <line x1="3" y1="12" x2="21" y2="12" />
                          <line x1="3" y1="17" x2="21" y2="17" />
                        </svg>
                      </span>
                    </div>
                    <div className="scroll">
                      <div className="app-doc">
                        <span className="private-tag">{tp("Private document", "Document privé")}</span>
                        <h2>VLADIMIR GRUDZINSKI</h2>
                        <div className="role">
                          {tp("Founder & CEO at CarJager", "Fondateur & CEO de CarJager")}
                          <br />
                          {tp("(Barnes CarJager) · Owner at", "(Barnes CarJager) · Propriétaire de")}
                          <br />
                          {tp("Prieuré de Conil · Founder at WooZoo", "Prieuré de Conil · Fondateur de WooZoo")}
                        </div>
                        <div className="meta">
                          <span>
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="8" r="4" />
                              <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                            </svg>{" "}
                            {tp("Age 40", "40 ans")}
                          </span>
                          <span>
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 21s-7-6.5-7-12a7 7 0 1114 0c0 5.5-7 12-7 12z" />
                              <circle cx="12" cy="9" r="2.5" />
                            </svg>{" "}
                            Aix-en-Provence
                          </span>
                        </div>
                        <div className="app-tribes">
                          <div className="tribes-label">{tp("Tribes", "Tribus")}</div>
                          <div className="tribes-list">
                            <span className="tribe" style={{ "--tc": "#C9A14A" } as CSSProperties}>
                              <span className="tdot" />
                              {tp("Tech Tribe", "Tribu Tech")}
                            </span>
                            <span className="tribe" style={{ "--tc": "#3A6E8F" } as CSSProperties}>
                              <span className="tdot" />
                              {tp("Business Tribe", "Tribu Business")}
                            </span>
                            <span className="tribe" style={{ "--tc": "#5C8A6E" } as CSSProperties}>
                              <span className="tdot" />
                              {tp("Hospitality Tribe", "Tribu Hospitality")}
                            </span>
                          </div>
                        </div>
                        <div className="app-statut">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                          </svg>{" "}
                          {tp("Official status", "Statut officiel")} <span className="id">ID #17713469</span>
                        </div>
                        <div className="app-actions">
                          <button>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" />
                              <path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
                            </svg>{" "}
                            {tp("Public link", "Lien public")}
                          </button>
                          <button className="primary">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>{" "}
                            {tp("Preview", "Aperçu")}
                          </button>
                        </div>
                      </div>
                      <div className="app-synth">
                        <h3>{tp("INVESTIGATION SUMMARY", "SYNTHÈSE DE L’ENQUÊTE")}</h3>
                        <p>
                          {tp(
                            "“Founder and CEO of CarJager (Barnes CarJager), the reference marketplace for classic and collection cars. Owner of Prieuré de Conil, a viticultural estate. Founder of WooZoo, the public digital identity platform…”",
                            "« Fondateur et CEO de CarJager (Barnes CarJager), la marketplace de référence pour les voitures de collection. Propriétaire du Prieuré de Conil, domaine viticole. Fondateur de WooZoo, la plateforme d’identité numérique publique… »",
                          )}
                        </p>
                        <ul>
                          <li>{tp("Member of Barnes International network", "Membre du réseau Barnes International")}</li>
                          <li>{tp("Entrepreneur in residence — automotive heritage", "Entrepreneur en résidence — patrimoine automobile")}</li>
                          <li>{tp("Founder of multiple ventures since 2015", "Fondateur de plusieurs entreprises depuis 2015")}</li>
                        </ul>
                      </div>
                      <div className="app-fade" />
                    </div>
                    <div className="app-tabbar">
                      <div className="tab">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9" />
                          <polyline points="12 7 12 12 15 14" />
                        </svg>
                        {tp("History", "Historique")}
                      </div>
                      <div className="tab active">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                          <circle cx="11" cy="11" r="7" />
                          <line x1="21" y1="21" x2="16" y2="16" />
                        </svg>
                        {tp("Search", "Recherche")}
                      </div>
                      <div className="tab">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                        </svg>
                        {tp("Profile", "Profil")}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="glare" />
              </div>
              <div className="callout top-left">
                <span className="dot" style={{ background: "#C9A14A" }} /> {tp("Tech Tribe", "Tribu Tech")}
              </div>
              <div className="callout top-right">
                <span className="dot" style={{ background: "#3A6E8F" }} /> {tp("Business Tribe", "Tribu Business")}
              </div>
              <div className="callout mid-right">
                <span className="dot" style={{ background: "#5C8A6E" }} /> {tp("Hospitality Tribe", "Tribu Hospitality")}
              </div>
              <div className="callout bot-left">
                <span className="dot" /> {tp("Verified sources", "Sources vérifiées")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================== EXPERIENCE ================== */}
      <section className="experience" id="experience">
        <div className="container">
          <div className="eyebrow">{t("03 · The Experience")}</div>
          <h2 className="display">
            {t("Two perspectives.")} <em style={emSerif}>{t("One gaze.")}</em>
          </h2>
          <p className="sub">
            {t("Reveal your own digital imprint, or read someone else's — same clarity, same structure.")}
          </p>

          <div className="two-perspectives">
            <div className="perspective">
              <div className="num">01</div>
              <h3>{t("For You")}</h3>
              <div className="role-line">{t("Start with yourself")}</div>
              <ol>
                <li>{t("Enter your name")}</li>
                <li>{t("Claim your profile")}</li>
                <li>{t("WooZoo reveals your public imprint")}</li>
                <li>{t("Submit authoritative sources to complete it")}</li>
                <li>{t("Share your card — as a structured signature")}</li>
              </ol>
            </div>
            <div className="perspective">
              <div className="num">02</div>
              <h3>{t("For Them")}</h3>
              <div className="role-line">{t("Read another profile")}</div>
              <ol>
                <li>{t("Search by name")}</li>
                <li>{t("Select the matching profile")}</li>
                <li>{t("Receive a structured public view")}</li>
                <li>{t("Read the synthesis and sources")}</li>
                <li>{t("Decide with context — never blindly")}</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ================== IDENTITY ================== */}
      <section className="identity" id="identity">
        <div className="container">
          <div className="eyebrow">{t("04 · Master your digital identity")}</div>
          <h2 className="display">
            {t("Your digital identity")} <em>{t("precedes")}</em> {t("you. It circulates. It informs. It")}{" "}
            <em>{t("influences")}</em>.
          </h2>

          <div className="pillars">
            <div className="pillar">
              <div className="pn">{t("A · Reveal")}</div>
              <h4>{t("As it manifests")}</h4>
              <p>{t("Clear. Structured. Objective. What is already visible — made legible.")}</p>
            </div>
            <div className="pillar">
              <div className="pn">{t("B · Refine")}</div>
              <h4>{t("Through real sources")}</h4>
              <p>{t("Submit authoritative references. Verified before integration. Never invented.")}</p>
            </div>
            <div className="pillar">
              <div className="pn">{t("C · Illuminate")}</div>
              <h4>{t("Don't transform")}</h4>
              <p>{t("You do not change reality. You ensure it is complete and well-lit.")}</p>
            </div>
          </div>

          <div className="closer">
            <p>{t('"You do not change reality. You illuminate it."')}</p>
            <a href="#" className="btn-ghost">
              {t("Explore my profile →")}
            </a>
          </div>
        </div>
      </section>

      {/* ================== WHEN ================== */}
      <section className="when" id="when">
        <div className="when-bg" aria-hidden="true" data-parallax="0.2">
          <svg viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
            <g fill="none" stroke="#A8BCC0" strokeOpacity="0.85" strokeWidth="6">
              {WHEN_RINGS.map((points, i) => (
                <polygon key={i} points={points} />
              ))}
            </g>
          </svg>
        </div>
        <div className="container">
          <div className="eyebrow">{t("05 · When to use WooZoo")}</div>
          <h2 className="display">
            {t("Before you meet.")} <em>{t("Before you decide.")}</em>
          </h2>

          <div className="when-list">
            <div className="when-row">
              <span className="n">01</span>
              <span className="t">{t("A personal encounter")}</span>
            </div>
            <div className="when-row">
              <span className="n">02</span>
              <span className="t">{t("A business meeting")}</span>
            </div>
            <div className="when-row">
              <span className="n">03</span>
              <span className="t">{t("A new partnership")}</span>
            </div>
            <div className="when-row">
              <span className="n">04</span>
              <span className="t">{t("A strategic hire")}</span>
            </div>
            <div className="when-row">
              <span className="n">05</span>
              <span className="t">{t("An investment")}</span>
            </div>
            <div className="when-row">
              <span className="n">06</span>
              <span className="t">{t("A worldly event")}</span>
            </div>
          </div>

          <div className="lighthouse">
            <div
              className="icon"
              role="img"
              aria-label="WooZoo"
              style={{ ...symbolMask, width: 170, height: 170, margin: "0 auto", display: "block", background: "var(--navy)" }}
            />
            <p>
              {t("Moving forward without context is navigating without reference.")}
              <br />
              <br />
              <strong>{t("WooZoo illuminates signals already visible —")}</strong>{" "}
              {t("like a lighthouse in the night.")}
            </p>
          </div>
        </div>
      </section>

      {/* ================== ETHICS ================== */}
      <section className="ethics" id="ethics">
        <div className="container">
          <div className="ethics-grid">
            <div>
              <div className="eyebrow">{t("06 · Transparency & responsibility")}</div>
              <h2 className="display">
                {t("WooZoo creates nothing.")}
                <br />
                {t("It")} <em style={emSerif}>{t("observes")}</em>
                {t(", structures, orders.")}
              </h2>
            </div>
            <div className="ethics-points">
              <div className="ethics-point">
                <div className="label">{t("A · Public only")}</div>
                <p>{t("We aggregate exclusively publicly available information — rendering visible what is already visible.")}</p>
              </div>
              <div className="ethics-point">
                <div className="label">{t("B · Regulated")}</div>
                <p>{t("In full respect of applicable regulatory frameworks. Always.")}</p>
              </div>
              <div className="ethics-point">
                <div className="label">{t("C · A clear gaze")}</div>
                <p>{t("Nothing more. Nothing less.")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================== PRICING ================== */}
      <section className="pricing" id="pricing">
        <div className="container">
          <div className="head">
            <div>
              <div className="eyebrow">{t("07 · Pricing")}</div>
              <h2 className="display">
                {t("Pay for what")} <em>{t("you reveal.")}</em>
              </h2>
            </div>
            <p>{t("One credit = one full profile analysis. Unused credits roll over within your billing month.")}</p>
          </div>

          <div className="plans">
            <div className="plan">
              <span className="badge">{t("One-shot")}</span>
              <h3>{t("Starter")}</h3>
              <div className="price-row">
                <span className="price">2,99€</span>
                <span className="per">{t("one-time")}</span>
              </div>
              <div className="credits">{t("1 search")}</div>
              <ul className="feat">
                <li>{t("One full profile reveal")}</li>
                <li>{t("Synthesis & sources")}</li>
                <li>{t("PDF export")}</li>
                <li>{t("Cancel anytime")}</li>
              </ul>
              <a href="#" className="cta">
                {t("Try a search →")}
              </a>
            </div>

            <div className="plan">
              <span className="badge">{t("Insider")}</span>
              <h3>{t("Insider")}</h3>
              <div className="price-row">
                <span className="price">7,99€</span>
                <span className="per">{t("/ month")}</span>
              </div>
              <div className="credits">{t("30 searches / month")}</div>
              <ul className="feat">
                <li>{t("Everything in Starter")}</li>
                <li>{t("Search history")}</li>
                <li>{t("Priority support")}</li>
                <li>{t("Cancel anytime")}</li>
              </ul>
              <a href="#" className="cta">
                {t("Choose Insider →")}
              </a>
            </div>

            <div className="plan featured">
              <span className="badge">{t("Most popular")}</span>
              <h3>{t("Elite")}</h3>
              <div className="price-row">
                <span className="price">49,99€</span>
                <span className="per">{t("/ month")}</span>
              </div>
              <div className="credits">{t("100 searches / month")}</div>
              <ul className="feat">
                <li>{t("Everything in Insider")}</li>
                <li>{t("Prospection contact info")}</li>
                <li>{t("Dedicated support")}</li>
                <li>{t("Advanced source submissions")}</li>
                <li>{t("Early access to new features")}</li>
              </ul>
              <a href="#" className="cta">
                {t("Choose Elite →")}
              </a>
            </div>
          </div>

          <div className="pricing-note">{t("Secure payment")}</div>
        </div>
      </section>

      {/* ================== B2B BANNER ================== */}
      <section className="b2b" id="b2b">
        <div className="b2b-inner">
          <div>
            <div className="label">{t("For organizations")}</div>
            <h3>{t("Bring WooZoo to your team — know your customers before the first interaction.")}</h3>
            <p>
              {t(
                "WooZoo Business — structured public-identity intelligence for hiring, partnerships, due-diligence and investor relations.",
              )}
            </p>
          </div>
          <div className="cta-side">
            <a href="#" className="btn-mint">
              {t("Discover WooZoo Business →")}
            </a>
          </div>
        </div>
      </section>

      {/* ================== CONTACT ================== */}
      <section className="contact" id="contact">
        <div className="container">
          <div className="contact-grid">
            <div>
              <div className="eyebrow">{t("08 · Contact us")}</div>
              <h2 className="display">{t("Let's talk.")}</h2>
              <p className="intro">{t("Press, partnerships, business — drop a note and we'll come back to you.")}</p>

              <div className="info">
                <div className="row">
                  <span className="k">{t("Email")}</span>
                  <a href="mailto:hello@woozoo.io">hello@woozoo.io</a>
                </div>
                <div className="row">
                  <span className="k">{t("Press")}</span>
                  <a href="mailto:press@woozoo.io">press@woozoo.io</a>
                </div>
                <div className="row">
                  <span className="k">{t("Instagram")}</span>
                  <a href="https://instagram.com/woozoo">@woozoo</a>
                </div>
              </div>
            </div>

            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="field">
                <label htmlFor="f-first">{t("First name")}</label>
                <input type="text" id="f-first" name="firstName" required />
              </div>
              <div className="field">
                <label htmlFor="f-last">{t("Last name")}</label>
                <input type="text" id="f-last" name="lastName" required />
              </div>
              <div className="field full">
                <label htmlFor="f-email">{t("Email")}</label>
                <input type="email" id="f-email" name="email" required />
              </div>
              <div className="field full">
                <label htmlFor="f-subject">{t("Subject")}</label>
                <select id="f-subject" name="subject">
                  <option>{t("General enquiry")}</option>
                  <option>{t("WooZoo Business")}</option>
                  <option>{t("Investors")}</option>
                  <option>{t("Press")}</option>
                  <option>{t("Partnership")}</option>
                  <option>{t("DPO")}</option>
                  <option>{t("Support")}</option>
                </select>
              </div>
              <div className="field full">
                <label htmlFor="f-msg">{t("Message")}</label>
                <textarea id="f-msg" name="message" required />
              </div>
              <div className="field full">
                <div style={{ marginBottom: 14 }}>
                  <CaptchaWidget status={captcha.status} onRetry={captcha.reset} />
                </div>
                <button type="submit" disabled={status === "sending"}>
                  {status === "sending"
                    ? t("Sending…")
                    : status === "sent"
                      ? t("Sent ✓")
                      : t("Send message →")}
                </button>
                {status === "sent" && (
                  <p className="form-note ok">{t("Thanks — we'll get back to you.")}</p>
                )}
                {status === "error" && (
                  <p className="form-note err">{t("Something went wrong. Please try again.")}</p>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ================== FOOTER ================== */}
      <footer>
        <div className="footer-inner">
          <div>{t("© WooZoo 2026 · A clear gaze")}</div>
          <div className="links">
            <a href="/legal/cgu">{t("Legals")}</a>
            <a href="/faq">{t("Help")}</a>
            <a href="/legal/privacy">{t("Privacy")}</a>
            {/* Intercepted by ConsentProvider's delegate; the href is the
                no-JS fallback, not a second destination. */}
            <a href="/legal/privacy#p-10" data-consent-open>
              {t("Cookies")}
            </a>
            <a href="/legal/suppression">{t("My data")}</a>
            <a href="https://instagram.com/woozoo">Instagram</a>
          </div>
        </div>
      </footer>
    </>
  );
}
